import type { Branding } from '@helios/ui';

type Props = {
  branding: Branding;
};

// IntelisHealth – clean AI healthcare aesthetic, inspired by their intelligence/data/workflow positioning.
// Intended to be passed as BrandedLoginShell's leftSlot (full left panel override).
export function HubIntelishealthLoginLeft({ branding }: Props) {
  // IntelisHealth-inspired default: modern clinical blue. Can still be overridden via tenant branding/theme.
  const primaryColor = branding.theme?.primaryColor ?? '#2563EB';

  // Clean AI-healthcare neutrals
  const azure = '#DBEAFE';
  const mint = '#D1FAE5';
  const ink = '#0F172A';
  const muted = '#475569';

  const features = [
    {
      title: 'Connected Care',
      body: 'Seamless collaboration across people and platforms.',
      icon: 'network',
    },
    {
      title: 'Smart Insights',
      body: 'Data-driven decisions for better outcomes.',
      icon: 'chart',
    },
    {
      title: 'Secure & Compliant',
      body: 'Enterprise-grade security you can trust.',
      icon: 'shield',
    },
  ];

  const brandName = branding.orgName || 'IntelisHealth';

  // Optional client hero image. Place the generated hero at:
  // apps/web-admin/public/clients/hub-intelishealth/hero.png
  const heroImage = '/clients/hub-intelishealth/hero.png';

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        // Clean AI-healthcare wash layers; hero visual is rendered as a masked layer below
        backgroundImage:
          `linear-gradient(90deg, rgba(239,246,255,0.98) 0%, rgba(239,246,255,0.92) 31%, rgba(219,234,254,0.34) 56%, rgba(219,234,254,0.00) 100%),` +
          `radial-gradient(980px 620px at 16% 12%, ${azure}F0 0%, ${azure}88 38%, ${azure}00 76%),` +
          `radial-gradient(920px 620px at 36% 88%, rgba(34,211,238,0.20) 0%, rgba(34,211,238,0.08) 36%, rgba(34,211,238,0.00) 72%),` +
          `linear-gradient(160deg, #EFF6FF 0%, #F8FBFF 42%, #EAF6FF 100%)`,
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat, no-repeat',
        backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: 'center, center, center, center',
      }}
    >
      {/* Soft intelligence/data-flow shapes */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        {/* Hero photo layer with feathered edges so it blends into the wash */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            backgroundImage: `url('${heroImage}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
            opacity: 1,
            filter: 'saturate(1.12) contrast(1.04)',
            // Feather the edges (works across modern browsers)
            WebkitMaskImage:
              'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.52) 28%, rgba(0,0,0,0.95) 48%, rgba(0,0,0,1) 100%)',
            maskImage:
              'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.52) 28%, rgba(0,0,0,0.95) 48%, rgba(0,0,0,1) 100%)',
          }}
        />

        {/* Left-side readability wash (prevents copy from sitting on top of the portrait) */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              'linear-gradient(90deg, rgba(239,246,255,0.96) 0%, rgba(239,246,255,0.88) 32%, rgba(219,234,254,0.45) 52%, rgba(219,234,254,0.00) 74%)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            backgroundPosition: 'left center',
          }}
        />

        {/* Extra edge wash to hide any hard photo boundaries on large screens */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage:
              `linear-gradient(180deg, rgba(239,246,255,0.70) 0%, rgba(239,246,255,0.00) 22%, rgba(239,246,255,0.72) 100%),` +
              `linear-gradient(90deg, rgba(219,234,254,0.00) 0%, rgba(219,234,254,0.00) 70%, rgba(219,234,254,0.34) 100%)`,
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundSize: '100% 100%, 100% 100%',
            backgroundPosition: 'center, center',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.28,
            filter: 'blur(70px)',
            backgroundColor: azure,
            width: '26rem',
            height: '26rem',
            top: '-8rem',
            left: '-8rem',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.18,
            filter: 'blur(70px)',
            backgroundColor: primaryColor,
            width: '20rem',
            height: '20rem',
            top: '18%',
            left: '18%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.14,
            filter: 'blur(80px)',
            backgroundColor: mint,
            width: '600px',
            height: '600px',
            bottom: '-14rem',
            left: '16%',
          }}
        />
      </div>

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        {/* Top nav (website-like) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.35rem 4rem 1.0rem 4rem',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {branding.orgLogoHorizontalUrl ? (
                <img
                  src={branding.orgLogoHorizontalUrl}
                  alt={brandName}
                  style={{
                    width: 'clamp(7.75rem, 10vw, 10.5rem)',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
              ) : branding.orgLogoUrl ? (
                <>
                  <img
                    src={branding.orgLogoUrl}
                    alt={brandName}
                    style={{
                      width: 'clamp(2.25rem, 3.4vw, 3.25rem)',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: ink }}>
                    {brandName}
                  </span>
                </>
              ) : (
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: ink }}>{brandName}</span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.75rem' }} />
        </div>

        {/* Lower copy section */}
        <div
          style={{
            padding: '2rem 4rem 3.25rem 4rem',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{ maxWidth: '44rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.55rem',
                alignSelf: 'flex-start',
                padding: '0.5rem 0.9rem',
                borderRadius: '9999px',
                backgroundColor: `${primaryColor}0F`,
                color: primaryColor,
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '1.35rem',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 3.5 13.35 8.25 18 9.6 13.35 10.95 12 15.5 10.65 10.95 6 9.6 10.65 8.25 12 3.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.5 14.5 19.2 17.05 21.75 17.75 19.2 18.45 18.5 21 17.8 18.45 15.25 17.75 17.8 17.05 18.5 14.5Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
              </svg>
              AI-powered healthcare intelligence
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(2.2rem, 2.8vw, 3.55rem)',
                lineHeight: 1.12,
                letterSpacing: '-0.04em',
                color: ink,
                fontWeight: 760,
                maxWidth: '38rem',
              }}
            >
              Intelligence designed
              <br />
              for the <span style={{ color: primaryColor }}>future</span> of
              <br />
              healthcare.
            </h1>

            <p
              style={{
                margin: '1.35rem 0 0 0',
                fontSize: '1.05rem',
                lineHeight: 1.58,
                color: muted,
                maxWidth: '36rem',
              }}
            >
              Connecting workflows, operations, patient journeys, and AI-driven insights across
              modern healthcare networks.
            </p>

            <div
              aria-hidden
              style={{
                marginTop: '1.55rem',
                width: '3.9rem',
                height: '0.22rem',
                borderRadius: '9999px',
                backgroundColor: primaryColor,
              }}
            />

            <div
              style={{
                marginTop: '1.8rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                maxWidth: '31rem',
              }}
            >
              {features.map((feature) => (
                <div
                  key={feature.title}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '3.35rem 1fr',
                    gap: '0.95rem',
                    alignItems: 'center',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      width: '3.35rem',
                      height: '3.35rem',
                      borderRadius: '9999px',
                      border: `1px solid ${primaryColor}22`,
                      backgroundColor: 'rgba(255,255,255,0.48)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: primaryColor,
                      boxShadow: '0 16px 38px rgba(37,99,235,0.08)',
                    }}
                  >
                    {feature.icon === 'network' ? (
                      <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
                        <circle cx="16" cy="8" r="4" stroke="currentColor" strokeWidth="2.2" />
                        <circle cx="8" cy="24" r="4" stroke="currentColor" strokeWidth="2.2" />
                        <circle cx="24" cy="24" r="4" stroke="currentColor" strokeWidth="2.2" />
                        <path
                          d="M14 11.5 9.8 20M18 11.5 22.2 20M12 24h8"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : feature.icon === 'chart' ? (
                      <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
                        <path
                          d="M6 25h20"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9 21v-5M16 21V10M23 21v-8"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                        />
                        <path
                          d="m8 12 6-5 5 4 5-7"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
                        <path
                          d="M16 4 25 8v7c0 6-3.8 10.5-9 13-5.2-2.5-9-7-9-13V8l9-4Z"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinejoin="round"
                        />
                        <path
                          d="m11.5 16 3 3 6-7"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 800,
                        color: ink,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {feature.title}
                    </div>
                    <div
                      style={{
                        marginTop: '0.2rem',
                        fontSize: '0.88rem',
                        lineHeight: 1.45,
                        color: muted,
                      }}
                    >
                      {feature.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginTop: 'auto',
              paddingTop: '2.5rem',
              fontSize: '0.875rem',
              color: '#94a3b8',
            }}
          >
            Powered by {branding.poweredBy ?? 'Intelis'}
          </div>
        </div>
      </div>
    </div>
  );
}
